import { notify } from '@affine/component';
import { AffineOtherPageLayout } from '@affine/component/affine-other-page-layout';
import { SignInPageContainer } from '@affine/component/auth-components';
import type { SignInStep } from '@affine/core/components/sign-in';
import { SignInPanel } from '@affine/core/components/sign-in';
import { SignInBackgroundArts } from '@affine/core/components/sign-in/background-arts';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  RouteLogic,
  useNavigateHelper,
} from '../../../components/hooks/use-navigate-helper';
import { useDesktopAppLayoutReady } from '../../bootstrap/use-desktop-app-layout-ready';
import {
  consumeSignInErrorQuery,
  SIGN_IN_ERROR_CODES,
  type SignInErrorCode,
} from './sign-in-error-query';

/**
 * Extract plan name from redirect_uri if it points to /subscribe?plan=...
 * Returns null if no plan context is present.
 */
function extractPlanFromRedirectUri(redirectUri: string | null): string | null {
  if (!redirectUri) return null;
  try {
    // redirect_uri is a relative path like /subscribe?plan=team
    const url = new URL(redirectUri, 'http://localhost');
    if (url.pathname === '/subscribe') {
      return url.searchParams.get('plan');
    }
  } catch {
    // Invalid URL — ignore
  }
  return null;
}

function parseInitialSignInStep(
  rawStep: string | null,
  hasServerOverride: boolean
): SignInStep {
  if (hasServerOverride) return 'addSelfhosted';
  return rawStep === 'signInWithEmail' ? 'signInWithEmail' : 'signIn';
}

function getSignInToastMessage(
  t: ReturnType<typeof useI18n>,
  errorCode: SignInErrorCode | null,
  fallbackMessage: string | null
): string | null {
  switch (errorCode) {
    case SIGN_IN_ERROR_CODES.APPLE_PRIVATE_RELAY_EMAIL_NOT_ALLOWED:
      return t['com.streamlain.auth.toast.message.apple-private-relay']();
    case SIGN_IN_ERROR_CODES.AUTH_UNEXPECTED_ERROR:
      return t['com.streamlain.auth.toast.message.unexpected']();
    case SIGN_IN_ERROR_CODES.GRACE_PERIOD_EXPIRED:
      return t['com.streamlain.auth.toast.message.grace-period-expired']();
    case SIGN_IN_ERROR_CODES.INVALID_CALLBACK_PARAMETERS:
      return t['com.streamlain.auth.toast.message.invalid-callback']();
    case SIGN_IN_ERROR_CODES.INVALID_MAGIC_LINK:
      return t['com.streamlain.auth.toast.message.invalid-magic-link']();
    case SIGN_IN_ERROR_CODES.INVALID_OAUTH_CALLBACK_PARAMETERS:
      return t['com.streamlain.auth.toast.message.invalid-oauth-callback']();
    case SIGN_IN_ERROR_CODES.INVALID_OAUTH_PARAMETERS:
      return t['com.streamlain.auth.toast.message.invalid-oauth-parameters']();
    case SIGN_IN_ERROR_CODES.INVALID_REACTIVATION_LINK:
      return t['com.streamlain.auth.toast.message.invalid-reactivation-link']();
    case SIGN_IN_ERROR_CODES.NETWORK_ERROR:
      return t['com.streamlain.auth.toast.message.network-error']();
    case SIGN_IN_ERROR_CODES.NOT_SCHEDULED:
      return t[
        'com.streamlain.auth.toast.message.reactivation-not-scheduled'
      ]();
    case SIGN_IN_ERROR_CODES.OAUTH_PROVIDER_REJECTED:
      return t['com.streamlain.auth.toast.message.oauth-provider-rejected']();
    case SIGN_IN_ERROR_CODES.REACTIVATION_TOKEN_INVALID:
      return t[
        'com.streamlain.auth.toast.message.reactivation-token-invalid'
      ]();
    case SIGN_IN_ERROR_CODES.REACTIVATION_TOKEN_STALE:
      return t['com.streamlain.auth.toast.message.reactivation-token-stale']();
    case SIGN_IN_ERROR_CODES.TOO_MANY_REQUEST:
      return t['com.streamlain.auth.toast.message.too-many-requests']();
    default:
      return fallbackMessage;
  }
}

export const SignIn = ({
  redirectUrl: redirectUrlFromProps,
}: {
  redirectUrl?: string;
}) => {
  const t = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { jumpToIndex } = useNavigateHelper();
  const [searchParams] = useSearchParams();
  const currentSearch = searchParams.toString();
  const redirectUrl = redirectUrlFromProps ?? searchParams.get('redirect_uri');
  const initialEmail = searchParams.get('email') ?? undefined;
  const intent = searchParams.get('intent') === 'invite' ? 'invite' : 'default';
  const handledErrorSearchRef = useRef<string | null>(null);

  const server = searchParams.get('server') ?? undefined;
  const step = searchParams.get('step');

  const planContext = useMemo(
    () => extractPlanFromRedirectUri(redirectUrl),
    [redirectUrl]
  );

  useEffect(() => {
    const { errorCode, message, nextSearch } =
      consumeSignInErrorQuery(searchParams);
    if (nextSearch === null) {
      handledErrorSearchRef.current = null;
      return;
    }

    if (handledErrorSearchRef.current === currentSearch) {
      return;
    }

    handledErrorSearchRef.current = currentSearch;

    const toastMessage = getSignInToastMessage(t, errorCode, message);
    if (toastMessage) {
      notify.error({
        title: t['com.streamlain.auth.toast.title.failed'](),
        message: toastMessage,
      });
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [currentSearch, location.pathname, navigate, searchParams, t]);

  const handleClose = useCallback(() => {
    jumpToIndex(RouteLogic.REPLACE, {
      search: searchParams.toString(),
    });
  }, [jumpToIndex, searchParams]);

  const handleAuthenticated = useCallback(
    (status: AuthSessionStatus) => {
      if (status === 'authenticated') {
        if (redirectUrl) {
          if (redirectUrl.toUpperCase() === 'CLOSE_POPUP') {
            window.close();
          }
          navigate(redirectUrl, {
            replace: true,
          });
        } else {
          handleClose();
        }
      }
    },
    [handleClose, navigate, redirectUrl]
  );

  const initStep = parseInitialSignInStep(step, Boolean(server));

  return (
    <SignInPageContainer>
      <SignInPanel
        onAuthenticated={handleAuthenticated}
        initStep={initStep}
        server={server}
        planContext={planContext}
        initialEmail={initialEmail}
        redirectUrl={redirectUrl}
        intent={intent}
      />
    </SignInPageContainer>
  );
};

export const Component = () => {
  useDesktopAppLayoutReady();

  return (
    <AffineOtherPageLayout
      showNavigation={false}
      showCollaborationCursors={false}
    >
      <SignInBackgroundArts />
      <SignIn />
    </AffineOtherPageLayout>
  );
};
